import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VideoItem, VodEditorial } from 'services/data-manager/types';
import { UavEvents, WatchUavParams } from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
    getTechnicalId,
} from 'services/uav/utils';

export type GetWatchUavParamsProps = Omit<
    WatchUavParams['metadata'],
    | 'appSessionId'
    | 'sessionId'
    | 'contentSource'
    | 'appName'
    | 'appVersion'
    | 'deviceType'
>;

const useGetWatchUavParams = (playbackType: StreamTypes | undefined) => {
    const appSessionId = useSelector(selectAppSessionId);
    const genericVideoSessionId = useSelector(selectVideoSessionId);
    const getBaseUavParams = useGetBaseUavParams();

    const getWatchUavParams = useCallback(
        (
            params: GetWatchUavParamsProps,
            videoItem?: VideoItem,
        ): WatchUavParams | undefined => {
            const baseUavParams = getBaseUavParams(UavEvents.WATCH);
            const seriesId = videoItem?.seriesId && {
                seriesId: videoItem.seriesId,
            };

            if (baseUavParams && appSessionId) {
                return {
                    ...baseUavParams,
                    metadata: {
                        ...params,
                        appSessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType: getPlayBackType(
                            playbackType,
                            videoItem?.originalProgram as
                                | VodEditorial
                                | RBProgram,
                        ),
                        playbackSessionId: genericVideoSessionId,
                        editorialID: videoItem?.id,
                        technicalId:
                            getTechnicalId(
                                videoItem?.originalProgram as
                                    | VodEditorial
                                    | RBProgram,
                            ) ?? videoItem?.id,
                        programmeId:
                            videoItem?.originalProgram?.id ?? videoItem?.id,
                        ...seriesId,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [getBaseUavParams, appSessionId, genericVideoSessionId, playbackType],
    );

    return {
        getWatchUavParams,
    };
};

export default useGetWatchUavParams;
